import { eq, object } from '@art-suite/art-core-ts';
import { createContext, ReactNode, useCallback, useContext, useMemo, useRef } from 'react';

import { createContextSubscribers } from '../contextSubscribers';

import {
  ApiOrgContractor,
  ApiOrgProduct,
  ApiOrgProperty,
  ApiPropertyProject,
  ApiScan,
  ApiScanNote,
  getProjectDerivedState,
  isProjectEditable,
  isScanLifecycleStatusAdminEditable,
  isScanLifecycleStatusFieldUserEditable,
  ProjectDerivedState,
  ScanScopeState,
} from '@kaiizen/kai-pro-api';
import {
  getEffectiveTaskTrade,
  isGroupShownOnScopeReport,
  isTaskShownOnScopeReport,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
  ScanTemplateRoot,
  taskHasScopeChanges,
} from '@kaiizen/kai-scan-template-system';

import { CachedQueryStatus, useCombinedCachedQueryStatus } from '@kaiizen/react-persistent-cache';
import {
  OrgPropertyMutators,
  PropertyProjectMutators,
  useAllOrgSettings,
  useAllProductSupplierOrgs,
  useOrgContractorsByOrgMarketId,
  useOrgProductsByIdForScan,
  useOrgProperty,
  usePropertyProject,
} from '../apiState';
import { useFeatureFlags } from '../apiState/useFeatureFlags';
import { useSaveScanData } from '../contexts';
import { getEnabledTaskModifiers, getKnownTaskModifiers, TaskModifier } from '../orgSettings';
import { useAuth } from '../useAuth';
import { ScanDataMutators, useScanDataMutators } from './ScanDataMutators';
import { userCanEditTurnBasedProjectWork } from './ScanEditingLib';
import { getTaskIdSlices, TaskIdSlices } from './TaskIdSlices';
import { ScanNoteMutators, useScanNotes, useScanNotesMutators } from './useScanNotes';
import { useOptimizedScanRoot } from './useScanRoot';
import { ScanMutators, useScan, useScansByPropertyProjectId } from './useScans';

export type ContractorsMap = Record<string, ApiOrgContractor>;

export type ScanEditingContextType = ProjectDerivedState & TaskIdSlices & {
  /**
   * @deprecated Use scanIsEditable instead (set userHasAdminScanAccess to true when creating ScanEditingContextProvider)
   */
  isAdminEditable: boolean;

  /**
   * @deprecated Use scanIsEditable instead (set userHasAdminScanAccess to true when creating ScanEditingContextProvider)
   */
  isFieldUserEditable: boolean;

  /**
   * scanIsEditable takes into account the current user's permissions and the scan's status.
   * currently, to signal admin access, set userHasAdminScanAccess to true when creating ScanEditingContextProvider
   */
  scanIsEditable: boolean;

  projectIsEditable: boolean;

  baselineDefaultContractorsByTrade?: Record<string, string | null>;
  baselineScanId?: string;
  baselineScanRoot?: ScanRootOptimized;
  contractors?: ApiOrgContractor[];
  contractorsMap?: ContractorsMap;
  defaultContractorsByTrade?: Record<string, string | null>;
  firstScan?: ApiScan;
  getContractorForTask: (task: ScanTaskOptimized) => ApiOrgContractor | undefined;

  latestApprovedScan?: ApiScan;
  latestScan?: ApiScan;
  orgMarketId?: string;
  /** The org's products by id, once loaded: what a scan's SKU'd parts price themselves from. */
  orgProductsById?: Record<string, ApiOrgProduct>;
  project?: ApiPropertyProject;
  propertyProjectId?: string;
  projectMutators: PropertyProjectMutators;
  property?: ApiOrgProperty;
  propertyMutators: OrgPropertyMutators;
  scan?: ApiScan;
  scans?: ApiScan[];
  scanDataMutators: ScanDataMutators;
  scanId: string;

  scanMutators: ScanMutators;
  scanNoteMutators: ScanNoteMutators;
  scanNotes: ApiScanNote[];
  scanNotesByScanPartId: Record<string, ApiScanNote[]>;
  scanRoot?: ScanRootOptimized;
  templateRoot?: ScanTemplateRoot;
  scanTemplateAt?: Date;
  scanTemplateId?: string;
  showScanChanges: boolean; // signal to the UX that we are comparing the current scan to a baseline scan (e.g. change order) (TODO: this should become the sole control for turning on and off any DIFF-related UI)
  /** What this user may apply, for the kebab picker. */
  enabledTaskModifiers: TaskModifier[];
  hasEnabledTaskModifiers: boolean;
  /** What may be on a line item, for anything that displays or filters. See `getKnownTaskModifiers`. */
  knownTaskModifiers: TaskModifier[];
  scanScopeState?: ScanScopeState;

  baselineScanWasCloseOut: boolean;
  firstApprovedCloseoutScan?: ApiScan;
} & CachedQueryStatus;

/**
 * Who will actually do a task: its explicit assignment, else the scan's default
 * for that trade, else the general default.
 *
 * Pure and exported so callers that hold the same inputs - tests, scripts, the
 * server - resolve a contractor exactly the way the app does, rather than
 * standing up a provider or reimplementing the fallback chain.
 */
export const getContractorForScanTask = (
  task: ScanTaskOptimized,
  { contractorsMap, defaultContractorsByTrade }: {
    contractorsMap?: ContractorsMap;
    defaultContractorsByTrade?: Record<string, string | null>;
  },
): ApiOrgContractor | undefined => {
  if (!contractorsMap) return undefined;
  const { assignedContractorId } = task;
  if (assignedContractorId) return contractorsMap[assignedContractorId];
  if (!defaultContractorsByTrade) return undefined;
  const trade = getEffectiveTaskTrade(task);
  const contractorId = defaultContractorsByTrade[trade as any] ?? defaultContractorsByTrade.general;
  return contractorId ? contractorsMap[contractorId] : undefined;
};

export const ScanEditingContext = createContext<ScanEditingContextType | null>(null);

const scanEditingSubscribers = createContextSubscribers<ScanEditingContextType>('ScanEditingContext');

const ScanEditingContextNotifier = ({ value }: { value: ScanEditingContextType }) => {
  const notify = scanEditingSubscribers.useNotifySubscribers();
  notify(value);
  return null;
};

const taskHasSomeLineItems = (
  task: ScanTaskOptimized,
  predicate: (lineItem: ScanLineItemOptimized) => boolean,
) => task.options?.some(option => option.lineItems?.some(predicate));

const taskHasLineItemsWithTag = (task: ScanTaskOptimized, tag: string) => taskHasSomeLineItems(task, lineItem => lineItem.tags?.[tag] === true);

export const getTasksChangedOrShownOnScope = ({
  groups,
  baselineScanRoot,
  defaultContractorsByTrade,
  baselineDefaultContractorsByTrade,
}: {
  groups?: ScanGroupOptimized[];
  baselineScanRoot?: ScanRootOptimized;
  defaultContractorsByTrade?: Record<string, string | null>;
  baselineDefaultContractorsByTrade?: Record<string, string | null>;
}) => {
  const scopedGroups = groups?.filter(isGroupShownOnScopeReport) ?? [];
  const baselineScopedGroupsById = !baselineScanRoot
    ? {}
    : object(scopedGroups, {
      withKey: ({ id }) => id,
      with: ({ id }: { id: string }) => baselineScanRoot.groupsById?.[id],
    });

  const baselineScopedTasksById: Record<string, ScanTaskOptimized> = {};
  const scopedTasks: ScanTaskOptimized[] = [];
  for (const group of scopedGroups) {
    for (const task of group.tasks ?? []) {
      if (isTaskShownOnScopeReport(task)) {
        scopedTasks.push(task);
        baselineScopedTasksById[task.id] = baselineScopedGroupsById[group.id]?.tasksById?.[task.id];
      }
    }
  }

  const changedScopedTasks = !baselineScanRoot
    ? []
    : scopedTasks.filter(task =>
      taskHasScopeChanges(
        task,
        baselineScopedTasksById[task.id],
        defaultContractorsByTrade,
        baselineDefaultContractorsByTrade,
      )
    );
  return {
    scopedTasks,
    changedScopedTasks,
  };
};

export const ScanEditingContextProvider = (
  { children, scanId, userHasAdminScanAccess }: { children: ReactNode; scanId: string; userHasAdminScanAccess?: boolean },
) => {
  userHasAdminScanAccess ||= false;

  const { userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const { scan, scanMutators, ...scanStatusInfo } = useScan(scanId);
  const propertyProjectId = scan?.propertyProjectId;
  const { optimizedScanRoot, optimizedBaselineScanRoot, updateCachedScanData, setOptimizedScanRoot, editSyncGate, ...optimizedScanRootStatusInfo } =
    useOptimizedScanRoot(scan);
  const { propertyProject: project, projectMutators, ...propertyProjectStatusInfo } = usePropertyProject(propertyProjectId);
  const { orgProperty, propertyMutators, ...orgPropertyStatusInfo } = useOrgProperty(project?.orgPropertyId);
  const saveScanData = useSaveScanData(scanId, optimizedScanRoot, updateCachedScanData, setOptimizedScanRoot, editSyncGate);
  const scanDataMutators: ScanDataMutators = useScanDataMutators(scan, saveScanData);
  const { scans } = useScansByPropertyProjectId(project?.id);

  const { allOrgSettings } = useAllOrgSettings();
  // `isFieldUser` is derived from `userHasAdminScanAccess` rather than a real user role
  // because the field-vs-admin distinction only matters where both audiences load this
  // provider - the KaiPro field app, which passes `userHasAdminScanAccess={false}`.
  // Renovator (web) is admin-only by definition: field-role users never load it, and both
  // of its provider mounts hardcode `userHasAdminScanAccess={true}` (App.tsx,
  // ProjectPage.tsx), so `isFieldUser` is always false and a modifier's `isEnabledFieldUser`
  // flag is (correctly) never enforced here. If a field-role audience ever loads Renovator,
  // pass the actual role through instead of the hardcoded `true`.
  const enabledTaskModifiers = useMemo(
    () =>
      getEnabledTaskModifiers(allOrgSettings?.taskSettings?.modifiers, {
        isFieldUser: !userHasAdminScanAccess,
      }),
    [allOrgSettings?.taskSettings?.modifiers, userHasAdminScanAccess],
  );
  const hasEnabledTaskModifiers = enabledTaskModifiers.length > 0;

  // Disabling a modifier takes it out of the picker above and nothing else: tagged line items
  // keep displaying and stay filterable, so every read-only surface uses this list instead.
  const knownTaskModifiers = useMemo(
    () => getKnownTaskModifiers(allOrgSettings?.taskSettings?.modifiers),
    [allOrgSettings?.taskSettings?.modifiers],
  );

  // subscribe to changes on latestScan, latestApprovedScan, firstScan, and firstApprovedCloseoutScan
  const { scan: latestScan } = useScan(project?.latestScan ?? project?.latestScanId);
  const { scan: latestApprovedScan } = useScan(project?.latestApprovedScan ?? project?.latestApprovedScanId);
  const { scan: firstScan } = useScan(project?.firstScan ?? project?.firstScanId);
  const { scan: firstApprovedCloseoutScan } = useScan(project?.firstApprovedCloseoutScan ?? project?.firstApprovedCloseoutScanId);

  const { contractors, contractorsMap } = useOrgContractorsByOrgMarketId(
    orgProperty?.orgMarketId || undefined,
  );

  const defaultContractorsByTrade = optimizedScanRoot?.defaultContractorsByTrade;
  const { scanNotesByScanPartId, scanNoteMutators, scanNotes } = useScanNotes(scanId);

  const combinedStatusInfo = useCombinedCachedQueryStatus(
    scanStatusInfo,
    optimizedScanRootStatusInfo,
    propertyProjectStatusInfo,
    orgPropertyStatusInfo,
  );

  // ****************************************************************
  // Helpers
  // ****************************************************************

  const getContractorForTask = useCallback(
    (task: ScanTaskOptimized) => getContractorForScanTask(task, { contractorsMap, defaultContractorsByTrade: optimizedScanRoot?.defaultContractorsByTrade }),
    [contractorsMap, optimizedScanRoot],
  );

  // ****************************************************************
  // Derived values
  // ****************************************************************
  const projectIsEditable = isProjectEditable(project);
  const derivedValuesRef = useRef<ProjectDerivedState | undefined>(undefined);
  const derivedValues = useMemo(
    () =>
      getProjectDerivedState({
        project,
        projectIsEditable,
        scan,
        latestScan,
        latestApprovedScan,
        firstScan,
        optimizedScanRoot,
        optimizedBaselineScanRoot,
        scanNotesByScanPartId,
        scans,
      }, derivedValuesRef.current),
    [
      projectIsEditable,
      scan,
      latestScan,
      latestApprovedScan,
      firstScan,
      optimizedScanRoot,
      optimizedBaselineScanRoot,
      scanNotesByScanPartId,
      scans,
    ],
  );
  derivedValuesRef.current = derivedValues;

  // ****************************************************************
  // Baseline scan derived values
  // ****************************************************************
  const baselineScanWasCloseOut = latestApprovedScan?.scanType === 'progressCompleted';

  // ***************************************************************
  // Task Slices
  // ***************************************************************
  const { productSupplierOrgs } = useAllProductSupplierOrgs();
  const thdOrgId = productSupplierOrgs?.homeDepot?.id;

  const { orgProductsById } = useOrgProductsByIdForScan(scan);
  const taskIdSlicesRef = useRef<TaskIdSlices | undefined>(undefined);
  const taskIdSlices = useMemo(() =>
    getTaskIdSlices({
      scanRoot: optimizedScanRoot,
      baselineScanRoot: optimizedBaselineScanRoot,
      approvalStatusByTaskId: derivedValues.approvalStatusByTaskId,
      orgProductsById,
      thdOrgId,
      previousTaskIdSlices: taskIdSlicesRef.current,
    }), [optimizedScanRoot, optimizedBaselineScanRoot, derivedValues.approvalStatusByTaskId, orgProductsById, thdOrgId]);
  taskIdSlicesRef.current = taskIdSlices;

  // ***************************************************************
  // User scan access
  // ***************************************************************
  const isAdminEditable = projectIsEditable && derivedValues.scanIsLatest && !!scan && isScanLifecycleStatusAdminEditable(scan.scanLifecycleStatus);
  const isFieldUserEditable = projectIsEditable && derivedValues.scanIsLatest && !!scan && isScanLifecycleStatusFieldUserEditable(scan.scanLifecycleStatus);
  const userCanEditCurrentProjectWork = userCanEditTurnBasedProjectWork({
    turnBasedHandoff: featureFlags.turnBasedHandoff,
    scan,
    effectiveScanType: derivedValues.effectiveScanType,
    responsibleUserId: project?.responsibleUserId,
    userId,
  });
  const scanIsEditable = (userHasAdminScanAccess ? isAdminEditable : isFieldUserEditable) && userCanEditCurrentProjectWork;

  const value: ScanEditingContextType = {
    ...derivedValues,

    // user scan access
    isAdminEditable,
    isFieldUserEditable,
    scanIsEditable,
    projectIsEditable,

    // baseline scan
    baselineDefaultContractorsByTrade: optimizedBaselineScanRoot?.defaultContractorsByTrade,
    baselineScanId: scan?.parentScanId ?? undefined,
    baselineScanRoot: optimizedBaselineScanRoot,

    // contractors
    contractors,
    contractorsMap,
    defaultContractorsByTrade,
    getContractorForTask,

    // market, project, property
    orgMarketId: orgProperty?.orgMarketId ?? undefined,
    orgProductsById,
    project: project,
    property: orgProperty,
    propertyProjectId,

    // mutators
    projectMutators,
    propertyMutators,
    scanDataMutators,
    scanMutators,
    scanNoteMutators,

    // notes
    scanNotes,
    scanNotesByScanPartId,

    // scan root
    scanRoot: optimizedScanRoot,
    templateRoot: optimizedScanRoot?.template,

    // template
    scanTemplateAt: scan?.scanTemplateAt ?? undefined,
    scanTemplateId: scan?.scanTemplateId ?? undefined,

    // UI
    showScanChanges: derivedValues.scanIsChangeOrder,
    enabledTaskModifiers,
    hasEnabledTaskModifiers,
    knownTaskModifiers,

    // scans
    scans,
    scanId,
    scan,
    firstScan,
    latestApprovedScan,
    latestScan,

    // baseline scan
    baselineScanWasCloseOut,

    // addendum/versioning
    firstApprovedCloseoutScan,

    // task slices
    ...taskIdSlices,

    // loading status
    ...combinedStatusInfo,
  };

  return (
    <scanEditingSubscribers.Provider>
      <ScanEditingContext.Provider value={value}>
        <ScanEditingContextNotifier value={value} />
        {children}
      </ScanEditingContext.Provider>
    </scanEditingSubscribers.Provider>
  );
};

export const useScanEditingContext = () => {
  const context = useContext(ScanEditingContext);
  if (!context) {
    throw new Error('useScanContext must be used within a ScanContextProvider');
  }
  return context;
};

export const useScanEditingContextOrUndefined = () => useContext(ScanEditingContext);

/**
 * Subscribe to a fixed set of fields from ScanEditingContext.
 * Re-renders only when one of the picked fields is Object.is-different
 * from the slice currently rendered.
 *
 * @example
 * const { scan, scanRoot } = useScanEditingContextSlice(['scan', 'scanRoot']);
 */
export function useScanEditingContextSlice<K extends keyof ScanEditingContextType>(
  fields: readonly K[],
): Pick<ScanEditingContextType, K> {
  return scanEditingSubscribers.useSlice(fields);
}

/**
 * Subscribe to a derived shape via a selector. The selector receives the
 * context value and the previous selector result, so it can return prev
 * sub-references when contents haven't changed (stabilizes identity for
 * downstream consumers). Pass `deps` to re-evaluate during render when
 * external state the selector closes over changes.
 *
 * @example
 * const { scanName } = useScanEditingContextSelector(c => ({ scanName: c.scan?.name }));
 *
 * @example
 * const { items } = useScanEditingContextSelector(
 *   ({ scanRoot }, prev) => {
 *     const next = computeItems(scanRoot);
 *     if (prev && shallowEqual(next, prev.items)) return { items: prev.items };
 *     return { items: next };
 *   },
 *   [searchRegex, groupingMode],
 * );
 */
export function useScanEditingContextSelector<R>(
  selector: (value: ScanEditingContextType, prev: R | undefined) => R,
  deps?: readonly unknown[],
): R {
  return scanEditingSubscribers.useSelector(selector, deps);
}

/**
 * Hook that provides a function to check if scan is editable and show toast if not
 * @returns checkEditableWithToast - function that returns true if editable, false if not (and shows toast)
 */
export const useScanIsEditableCheck = (showToast: (message: string, severity?: 'error' | 'success' | 'info' | 'warning') => void) => {
  const { scanIsEditable, scan } = useScanEditingContext();

  const checkEditableWithToast = useCallback((): boolean => {
    if (!scanIsEditable) {
      showToast(`Scans in the ${scan?.scanLifecycleStatus || 'unknown'} status cannot be edited`, 'error');
      return false;
    }
    return true;
  }, [scanIsEditable, scan?.scanLifecycleStatus, showToast]);

  return checkEditableWithToast;
};

export const useScanPartNotes = (scanPartId?: string) => {
  const { scanPartNotes, scanId } = useScanEditingContextSelector<{ scanPartNotes: ApiScanNote[]; scanId: string }>(
    ({ scanNotesByScanPartId, scanId }, previousState) => {
      const scanPartNotes = scanPartId ? scanNotesByScanPartId[scanPartId] || [] : [];
      if (eq(scanPartNotes, previousState?.scanPartNotes)) {
        return previousState!;
      }
      return { scanPartNotes, scanId };
    },
    [scanPartId],
  );
  const scanNoteMutators = useScanNotesMutators(scanId);

  return {
    scanPartNotes,
    scanNoteMutators,
  };
};
